import React from 'react'
import {Box} from '@chakra-ui/react'
import PropTypes from 'prop-types'

/* Arrow visibility is authoring-driven via per-view-type Arrows config. The
 * caller computes whether arrows should be visible per-breakpoint (base/md/lg)
 * plus a desktop hover override and passes those flags so this builder can
 * produce the correct sx. Hover-to-reveal applies only at lg (touch devices
 * have no hover); on mobile/tablet visibility is purely authoring-driven. */
export const buildArrowSx = (visibleDefaultByBp, visibleHover) => {
    const toResponsive = (v) =>
        v && typeof v === 'object'
            ? {base: !!v.base, md: !!v.md, lg: !!v.lg}
            : {base: !!v, md: !!v, lg: !!v}
    const visible = toResponsive(visibleDefaultByBp)
    const sx = {
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        transform: 'translateY(-50%)',
        opacity: {
            base: visible.base ? 1 : 0,
            md: visible.md ? 1 : 0,
            lg: visible.lg ? 1 : 0
        },
        transition: 'opacity 0.2s ease, background-color 0.2s ease',
        '&:hover': {backgroundColor: 'webTheme.backgroundDefault'}
    }
    if (visible.lg !== visibleHover) {
        sx['[data-block-type="GDICarouselGridBlock"]:hover &'] = {
            opacity: visibleHover ? 1 : 0
        }
    }
    return sx
}

/* Square scroll-arrow button. `display` is driven by the caller (defaults to
 * desktop-only) so authors can opt mobile/tablet arrows in via the
 * per-view-type `Arrows` field. Visibility against the carousel edge state
 * is still controlled by the external `hidden` flag. */
export const ArrowButton = ({
    label,
    onClick,
    side,
    hidden,
    icon,
    sx,
    display = {base: 'none', lg: 'flex'},
    buttonRef
}) => (
    <Box
        ref={buttonRef}
        as="button"
        type="button"
        aria-label={label}
        aria-hidden={hidden ? 'true' : undefined}
        tabIndex={hidden ? -1 : 0}
        onClick={onClick}
        display={display}
        visibility={hidden ? 'hidden' : 'visible'}
        position="absolute"
        top="50%"
        {...{[side]: 0}}
        zIndex={2}
        width="40px"
        height="40px"
        alignItems="center"
        justifyContent="center"
        cursor="pointer"
        border="none"
        padding={0}
        backgroundColor="rgba(255, 255, 255, 0.5)"
        sx={sx}
    >
        {icon}
    </Box>
)

ArrowButton.propTypes = {
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    side: PropTypes.oneOf(['left', 'right']).isRequired,
    hidden: PropTypes.bool,
    icon: PropTypes.node.isRequired,
    sx: PropTypes.object.isRequired,
    display: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    buttonRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object])
}
